/** The state colour law. One place, used by every component. */
export type Tone = 'refusal' | 'normal' | 'waiting' | 'progress' | 'inactive'

const WORDS: Record<Exclude<Tone, 'inactive'>, string[]> = {
  /* 'Not quotable' is a documents file whose text could not be made searchable: it was read
     and is trained on, but a model cannot cite it, and that is a refusal the customer acts on
     (W8-DS-16 T3). */
  refusal: ['Failed', 'Did not finish', 'Not readable', 'Not quotable', 'Not accepted', 'Rejected', 'Refused', 'Removed', 'Cancelled', 'Canceled', 'Stopped', 'Expired', 'Revoked', 'Overdue', 'Delinquent', 'Suspended'],
  normal: ['Ready', 'Live', 'Done', 'Current', 'Indexed', 'On', 'Passed', 'Healthy', 'Active', 'Paid', 'Forgiven', 'Set'],
  /* 'Older text' is a document whose newest text is not quotable yet while the previous one
     still is: the model answers, but not from what was last uploaded (W8-DS-16 T3). It is not a
     refusal — nothing is lost — and not the green of a file that is fully current. */
  waiting: ['Older text', 'Pending', 'Queued', 'Waiting', 'Waited', 'Paused', 'Needs review', 'Needs input', 'Partly read', 'Not started', 'Held', 'Due', 'Draft', 'Expiring', 'Credit exhausted'],
  progress: ['Reading', 'Parsing', 'Uploading', 'Receiving', 'Checking', 'Extracting', 'Creating', 'Reindexing',
    'Indexing', 'Warming', 'Starting', 'Waking', 'Building', 'Training', 'Writing', 'Syncing', 'Running',
    /* Preparation is the first stage of training and is work in flight, so it wears the colour
       of work rather than the grey of a word nobody claimed (founder, 2026-09-21). */
    'Preparing', 'Evaluating', 'Answering', 'Rolling out', 'Deploying'],
}

export function toneOf(status: string): Tone {
  const s = status.trim().toLowerCase()
  for (const tone of ['refusal', 'normal', 'waiting', 'progress'] as const)
    if (WORDS[tone].some(w => s.startsWith(w.toLowerCase()))) return tone
  return 'inactive'
}

export const TONE_CLASS: Record<Tone, { text: string; dot: string; glow: string; bar: string }> = {
  refusal:  { text: 'text-destructive', dot: 'bg-destructive', glow: 'shadow-glow-destructive', bar: 'bg-destructive/55' },
  normal:   { text: 'text-primary',     dot: 'bg-primary',     glow: 'shadow-glow-primary',     bar: 'bg-primary/55' },
  waiting:  { text: 'text-warning',     dot: 'bg-warning',     glow: 'shadow-glow-warning',     bar: 'bg-warning/55' },
  progress: { text: 'text-info',        dot: 'bg-info',        glow: 'shadow-glow-info',        bar: 'bg-info/55' },
  inactive: { text: 'text-muted-foreground', dot: 'bg-muted-foreground', glow: 'shadow-glow-muted', bar: 'bg-muted' },
}

export const isRunning = (status: string) => toneOf(status) === 'progress'

/**
 * Contract → screen words. Every enum the API returns has exactly one label here,
 * so a page never invents a status. Tones follow from the label via toneOf().
 */
export const DATA_WORDS = {
  revision:   { uploading: 'Uploading', finalizing: 'Receiving', finalized: 'Ready', expired: 'Expired' },
  /* The container's own word. `empty` is a typed container with nothing in it yet — a calm,
     inactive state, not a failure and not work in flight. `building` is a version being
     prepared. `canceled` is a build the customer stopped from the dataset page, and reads as a
     refusal like every other stop. The three storage words belong to the single prepared file
     of a pre-container dataset. */
  dataset:    { empty: 'Empty', building: 'Building', ready: 'Ready', failed: 'Failed', uploading: 'Uploading', upload_failed: 'Failed', validating: 'Preparing' },
  /* Where a dataset stands, as the server decided it. `status` says what the container stores;
     this says what a customer is told, and the portal does not combine the two — one word per
     state, read from `readiness.state` and shown wherever a dataset is shown (W8-DS-26). */
  datasetReadiness: { empty: 'Empty', uploading: 'Uploading', writing: 'Writing', syncing: 'Syncing', preparing: 'Preparing', checking: 'Checking', unreadable: 'Not readable', building: 'Building', ready: 'Ready', failed: 'Failed' },
  datasetVersion: { building: 'Building', ready: 'Ready', failed: 'Failed', canceled: 'Cancelled' },
  /* Whether a memory collection can be quoted from, as the server decided it. The collection row
     is born `ready` meaning "ready to accept documents", so the stored status is not the word a
     customer reads; this is (W8-UI-09). `Not quotable` is a collection whose documents could not
     be made searchable — the same word a single unquotable file wears. */
  /* A deployment's own word. `waiting_for_documents` is an endpoint with a chosen version and
     declared documents that cannot answer until those documents are quotable; it was being shown
     as `Paused`, which is what an endpoint taken offline by its owner wears (W8-UI-09). */
  deployment: { active: 'Live', offline: 'Paused', waiting_for_documents: 'Waiting for documents' },
  /* A memory document's own word. `queued` is a document the platform has not finished reading
     into memory yet — work in flight, the word the collection's former `parsing` wore. */
  document:   { queued: 'Reading', ready: 'Ready', failed: 'Failed' },
  memoryReadiness: { empty: 'Empty', preparing: 'Creating', indexing: 'Indexing', reindexing: 'Reindexing', unquotable: 'Not quotable', ready: 'Ready', failed: 'Failed' },
  datasetSource:  { idle: 'Ready', syncing: 'Syncing', failed: 'Failed' },
  /* A member's own word. `removed` is a file deleted from the project while the dataset still
     holds it: terminal, and the only one of the four that is not a reading state at all. It used
     to arrive as `checking`, so a dataset whose file was gone said its files were still being
     read (W8-UI-10). */
  member:     { ready: 'Ready', checking: 'Checking', refused: 'Not readable', removed: 'Removed' },
  /* The entries a training run records about itself. `settled` is the moment an attempt
     ended, and the word it wears is the end it reached — the same three words the version
     itself wears, so the journal and the run card cannot say different things about one
     outcome. `relaunched` is the platform putting the run back on a machine after losing
     one; `charge_waived` is a past platform failure whose charge was taken back. */
  trainingEvent:   { settled: 'Finished', relaunched: 'Started again', charge_waived: 'Charge waived' },
  trainingOutcome: { succeeded: 'Ready', failed: 'Failed', cancelled: 'Stopped' },
} as const

/**
 * One entry of a training run's chain, in one word. A settled entry says which end it
 * reached; every other entry says what happened. An unrecognised value is never invented
 * into a sentence — it stays the honest "Not available".
 */
export function trainingExecutionWord(event: string, state?: string | null): string {
  if (event === 'settled' && state) return DATA_WORDS.trainingOutcome[state as keyof typeof DATA_WORDS.trainingOutcome] ?? 'Not available'
  return DATA_WORDS.trainingEvent[event as keyof typeof DATA_WORDS.trainingEvent] ?? 'Not available'
}
